import { inject, Injectable, signal, WritableSignal } from '@angular/core';

import { FilterSectionTypes } from '../../enums/inventory-management.enum';
import { BehaviorSubject, Observable } from 'rxjs';
import { selectedGroupMemberUuids } from '../lead-seller-group/reducer';
import { LeadSellerGroupService } from '../lead-seller-group/lead-seller-group.service';
import {
  InventoryManagementDealerFilter,
  InventoryManagementState,
  LaunchDarklyService,
  StatusFilter, UpdateCurrentStatus, UpdateSearchCriteria, UpdateSearchRequest,
  UserProfileState,
  UserRole
} from 'remarketing-angular-library';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { select, Store } from '@ngxs/store';
import { SearchFilterSectionService } from '../search-filter-section/search-filter-section.service';
import { SearchFilterFactory } from '../../factories/search-filter.factory';
import { RouterService, SaService } from 'smartauction-frontend-micro-library';

enum ParamTypes {
  FILTER_BY_STATUS = 'filterByStatus',
  FILTER_BY_USER = 'filterByUser',
  FILTER_BY_DEALER = 'filterByDealer',
  FILTER_BY_IN_SERVICE = 'filterByInService'
}

@Injectable()
export class SearchFilterParametersService extends SaService {

  private readonly leadSellerGroupService: LeadSellerGroupService;
  private readonly router: Router;
  private readonly launchDarklyService: LaunchDarklyService;
  private readonly store: Store;
  private readonly searchFilterSectionService: SearchFilterSectionService;
  private readonly searchFilterFactory: SearchFilterFactory;
  private readonly routerService: RouterService;

  private paramsComplete$: WritableSignal<boolean> = signal(false);
  private leadSellerRequestMade$: BehaviorSubject<boolean>;
  private selectedGroupMemberUuid$: Observable<string[]>;
  private readonly inventoryManagementSearchRequest$ = select(InventoryManagementState.searchRequest);
  private readonly inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);
  private readonly userUuid$ = select(UserProfileState.userUuid);

  constructor() {
    super();
    this.leadSellerGroupService = inject(LeadSellerGroupService);
    this.launchDarklyService = inject(LaunchDarklyService);
    this.store = inject(Store);
    this.router = inject(Router);
    this.searchFilterSectionService = inject(SearchFilterSectionService);
    this.searchFilterFactory = inject(SearchFilterFactory);
    this.routerService = inject(RouterService);
  }

  private get isInServiceVehiclesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  private handleParameters(): void {
    const filterByStatus = this.routerService.getQueryParamValue(ParamTypes.FILTER_BY_STATUS);
    const filterByUser = this.routerService.getQueryParamValue(ParamTypes.FILTER_BY_USER);
    const filterByDealer = this.routerService.getQueryParamValue(ParamTypes.FILTER_BY_DEALER);
    const filterByInService = this.routerService.getQueryParamValue(ParamTypes.FILTER_BY_IN_SERVICE);

    let defaultSearchCriteria = { ...this.inventoryManagementSearchCriteria$() };
    // clear vins, stock numbers, and accountManagers
    if (this.routerService.queryParamsExist) {
      defaultSearchCriteria.stockNumbers = [];
      defaultSearchCriteria.vins = [];
      defaultSearchCriteria.filterByEmployee = undefined;
    }

    if (filterByStatus) {
      // map the plain text filter param to the enum equivalent
      const filterByStatusMap = {
        ['ACTIVE']: StatusFilter.ACTIVE_VEHICLES,
        ['SOLD']: StatusFilter.SOLD_VEHICLES,
        ['HOLD']: StatusFilter.HOLD_VEHICLES,
        ['REMOVED']: StatusFilter.REMOVED_VEHICLES,
        ['ALL']: StatusFilter.ALL_VEHICLES,
        ['SHARED']: StatusFilter.SHARED_VEHICLES,
        ['RESERVED']: StatusFilter.RESERVED_VEHICLES
      };

      // spreader to set the search criteria to use the default settings for a specific top level filter status
      defaultSearchCriteria = {
        ...defaultSearchCriteria,
        ...this.searchFilterFactory.buildDefaultFilter(filterByStatusMap[filterByStatus])
      };
      if (this.isInServiceVehiclesEnabled) {
        defaultSearchCriteria.filterByInService = true;
      }
      this.store.dispatch(new UpdateCurrentStatus(filterByStatusMap[filterByStatus]));
    }

    if (filterByUser) {
      // If we're filtering by user, we swap to the shared list default settings
      defaultSearchCriteria = {
        ...defaultSearchCriteria,
        ...this.searchFilterFactory.buildDefaultFilter(StatusFilter.SHARED_VEHICLES)
      };
      this.store.dispatch(new UpdateCurrentStatus(StatusFilter.SHARED_VEHICLES));
      defaultSearchCriteria.offerListCreators = [this.userUuid$()];
      defaultSearchCriteria.dealersMadeOffers = null;
      defaultSearchCriteria.dealersAskedQuestions = null;
      defaultSearchCriteria.dealersWonVehicles = null;
      defaultSearchCriteria.filterByDealer = InventoryManagementDealerFilter.OFFERS_BY_DEALER;
      if (this.isInServiceVehiclesEnabled) {
        defaultSearchCriteria.filterByInService = true;
        if (this.searchFilterSectionService.getOpenFilterSection() !== FilterSectionTypes.SHARED_LISTS) {
          this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.SHARED_LISTS);
        }
      } else {
        if (this.searchFilterSectionService.getOpenFilterSection() !== FilterSectionTypes.OFFERS) {
          this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.OFFERS);
        }
      }
    }

    if (filterByDealer) {
      switch (filterByDealer) {
      case InventoryManagementDealerFilter.OFFERS_BY_ANY_DEALER:
        if (this.searchFilterSectionService.getOpenFilterSection() !== FilterSectionTypes.OFFERS) {
          this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.OFFERS);
        }
        defaultSearchCriteria.filterByDealer = InventoryManagementDealerFilter.OFFERS_BY_ANY_DEALER;
        if (this.isInServiceVehiclesEnabled) {
          defaultSearchCriteria.filterByInService = false;
        }
        break;
      case InventoryManagementDealerFilter.QUESTIONS_BY_ANY_DEALER:
        if (this.searchFilterSectionService.getOpenFilterSection() !== FilterSectionTypes.QUESTIONS) {
          this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.QUESTIONS);
        }
        defaultSearchCriteria.filterByDealer = InventoryManagementDealerFilter.QUESTIONS_BY_ANY_DEALER;
        break;
      default:
        // do nothing
      }
    }

    if (filterByInService) {
      defaultSearchCriteria.filterByInService = (filterByInService === 'true');
    }

    // update search request
    this.store.dispatch(new UpdateSearchRequest({
      ...this.inventoryManagementSearchRequest$(),
      page: 0,
      searchCriteria: defaultSearchCriteria
    }));

  }

  private handleSamePageParams(): void {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd && e.url.includes('?')),
        tap(() => {
          this.handleParameters();
          this.routerService.clearParams();
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }

  private processAndCompleteParameters(selectedMemberUuids: string[] = []) {
    const maintainPreviousSearch = this.store.selectSnapshot(InventoryManagementState.shouldMaintainPreviousSearchInState);
    // Execute a default search with parameters if we're not naving back from the VDP, offers, or questions pages
    if (!maintainPreviousSearch || this.routerService.queryParamsExist) {
      this.store.dispatch(new UpdateSearchCriteria({
        ...this.inventoryManagementSearchCriteria$(),
        leadSellerGroupMembers: selectedMemberUuids
      }));
      this.handleParameters();
    }
    this.handleSamePageParams();
    this.paramsComplete$.set(true);
    this.routerService.clearParams();
  }

  public handleInitialSearch() {
    this.leadSellerRequestMade$ = this.leadSellerGroupService.getLeadSellerResponse$();
    this.selectedGroupMemberUuid$ = this.leadSellerGroupService.select(selectedGroupMemberUuids);

    const isLeadSeller = this.store.selectSnapshot(UserProfileState.fineGrainedRoles).includes(UserRole.LEAD_SELLER);

    if (isLeadSeller) {
      this.leadSellerRequestMade$
        .pipe(
          filter(ready => ready),
          take(1)
        )
        .subscribe(() => {
          this.selectedGroupMemberUuid$.pipe(take(1)).subscribe(selectedMemberUuids => {
            this.processAndCompleteParameters(selectedMemberUuids);
          });
        });
    } else {
      this.processAndCompleteParameters();
    }
  }

  public areParamsComplete() {
    return this.paramsComplete$();
  }

}
